import type { FunctionComponent, ReactNode } from 'react'
import styled, { css } from 'styled-components'

interface HeadingProps {
  children?: ReactNode
  id?: string
  type?: string
  sub?: boolean
  large?: boolean
  style?: object
  onClick?: () => void
}

const StyledH1 = styled.h1`
  margin-bottom: 1.25rem;
  font-size: 1.5rem;
  line-height: 1.25;

  @media screen and (min-width: 64rem) {
    font-size: 1.875rem;
  }
`
const StyledH2 = styled.h2<{ $sub?: boolean }>`
  font-size: 1.25rem;
  line-height: 1.4;
  margin-bottom: 0.5rem;
  @media screen and (min-width: 64rem) {
    font-size: 1.5rem;
    line-height: 1.25;
  }
  ${({ $sub }) =>
    $sub &&
    css`
      color: var(--red);
      font-weight: 700;
      margin-bottom: 0;
    `}
`
const StyledH3 = styled.h3<{ $sub?: boolean; $large?: boolean }>`
  font-size: 1.25rem;
  line-height: 1.4;
  margin: 0 0 0.5rem;
  ${({ $sub }) =>
    $sub &&
    css`
      color: var(--red);
      font-weight: 700;
      margin: 0;
    `}
  ${({ $large }) =>
    $large &&
    css`
      font-size: 1.5rem;
      line-height: 1.3;
      font-weight: 800;
      margin: 0;
      @media screen and (min-width: 48rem) {
        font-size: 2.5rem;
        line-height: 1.1;
      }
    `}
`
const StyledH4 = styled.h4`
  font-size: 1.125rem;
  line-height: 1.4;
  margin: 0 0 0.5rem;
`
const StyledH5 = styled.h5`
  color: var(--blue);
  font-size: 1.125rem;
  line-height: 1.4;
  margin: 0;
`
const StyledH6 = styled.h6`
  font-size: 1rem;
  line-height: 1.25;
  font-weight: 500;
`

const StyledEm = styled.em`
  font-size: 1rem;
`

const Heading: FunctionComponent<HeadingProps> = ({
  children,
  id,
  large,
  type,
  sub,
  style,
  ...otherProps
}) => {
  switch (type) {
    case 'h2':
      return (
        <StyledH2 id={id} style={style} {...otherProps}>
          {children}
        </StyledH2>
      )
    case 'h3':
      return (
        <StyledH3 id={id} $sub={sub} style={style} $large={large} {...otherProps}>
          {children}
        </StyledH3>
      )
    case 'h4':
      return (
        <StyledH4 id={id} style={style} {...otherProps}>
          {children}
        </StyledH4>
      )
    case 'h5':
      return (
        <StyledH5 id={id} style={style} {...otherProps}>
          {children}
        </StyledH5>
      )
    case 'h6':
      return (
        <StyledH6 id={id} style={style} {...otherProps}>
          {children}
        </StyledH6>
      )
    case 'em':
      return (
        <StyledEm id={id} style={style} {...otherProps}>
          {children}
        </StyledEm>
      )
    case 'h1':
    default:
      return (
        <StyledH1 id={id} style={style} {...otherProps}>
          {children}
        </StyledH1>
      )
  }
}

export default Heading
